import { Box, Checkbox, FormControlLabel, IconButton, InputAdornment, Link, MenuItem } from "@mui/material";
import React, { useEffect } from "react";
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeft from '@mui/icons-material/ArrowCircleLeft';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { 
  GradientBackground, 
  CenteredContainer, 
  GlassCard, 
  StyledCardContent, 
  LogoCircle, 
  GradientTitle, 
  StyledSubtitle, 
  StyledInputLabel, 
  StyledInput, 
  StyledSelect, 
  FormContainer, 
  ActionsContainer 
} from "../../components/styled/StyledComponents";
import { useLoginController } from "./context/login-controller";
import { useLoginState } from "./context/login-context";
import { useGlobalState } from "../global/context/global-context";
import { useNavigate } from "react-router-dom";
import { useBaseLayoutState } from "../base-layout/context/base-layout-context";

export const Login = () => {
    const { authentication, handleOnChange, resetState, setShowPassword, setInfoAuthentication } = useLoginController();
    const { preAuthenticationRequest, preAuthenticationResponse, authenticationRequest, fieldIsValid, showPassword, authenticationResponse } = useLoginState();
    const { loading } = useBaseLayoutState();
    const { applicationData } = useGlobalState();
    const navigate = useNavigate();

    useEffect(() => {
        if (setInfoAuthentication()) {
            navigate(`/${applicationData?.initialPage}`);
        }
    }, [authenticationResponse])

    return (
        <React.Fragment>
            <GradientBackground>
                <CenteredContainer maxWidth={'sm'}>
                    <GlassCard variant="outlined">
                        <StyledCardContent>
                            <FormContainer>
                                {/* Header com logo e título */}
                                <Box sx={{ textAlign: 'center', mb: 1 }}>
                                    <LogoCircle>M</LogoCircle>
                                    <GradientTitle variant="h4">
                                        {applicationData?.moduleDescription}
                                    </GradientTitle>
                                    <StyledSubtitle variant="body1">
                                        Faça o login com seu e-mail
                                    </StyledSubtitle>
                                </Box>
                                {/* Tipo de Login */}
                                <Box sx={{ display: applicationData?.moduleId === 2 ? 'block' : 'none', mb: 1 }}>
                                    <StyledInputLabel>Tipo de Login</StyledInputLabel>
                                    <StyledSelect name="select-type"
                                        defaultValue={1}
                                        value={preAuthenticationRequest?.type}
                                        error={!fieldIsValid && preAuthenticationRequest?.type === undefined ? true : false}
                                        fullWidth 
                                        onChange={handleOnChange} 
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                    >
                                        <MenuItem value={1} key={1}>Entidade</MenuItem>
                                        <MenuItem value={2} key={2}>Empresa</MenuItem>
                                    </StyledSelect>
                                </Box>
                                {/* E-mail */}
                                <Box sx={{ mb: 1 }}>
                                    <StyledInputLabel>E-mail</StyledInputLabel>
                                    <StyledInput
                                        name="input-user"
                                        fullWidth
                                        placeholder="Digite seu e-mail"
                                        error={!fieldIsValid && (preAuthenticationRequest?.email === undefined || preAuthenticationRequest?.email.length === 0) ? true : false}
                                        value={preAuthenticationRequest?.email}
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                        onChange={handleOnChange}
                                    />
                                </Box>
                                {/* Senha */}
                                <Box sx={{ mb: 1 }}>
                                    <StyledInputLabel>Senha</StyledInputLabel>
                                    <StyledInput
                                        name="input-password"
                                        value={preAuthenticationRequest?.password}
                                        placeholder="Digite sua senha"
                                        error={!fieldIsValid && (preAuthenticationRequest?.password === undefined || preAuthenticationRequest?.password.length === 0) ? true : false}
                                        fullWidth
                                        onChange={handleOnChange}
                                        type={showPassword ? 'text' : 'password'}
                                        disabled={(preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0) || loading ? true : false}
                                        endAdornment={<InputAdornment position='end'>
                                            <IconButton
                                                aria-label='toggle password visibility'
                                                onClick={() => {
                                                    setShowPassword(!showPassword);
                                                }}>
                                                {!showPassword && <VisibilityIcon />}
                                                {showPassword && <VisibilityOffIcon />}
                                            </IconButton>
                                        </InputAdornment>}
                                    />
                                </Box>
                                {/* Seleção de Empresa/Entidade */}
                                <Box sx={{ display: preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 ? 'block' : "none", mb: 1 }}>
                                    <StyledInputLabel>{preAuthenticationRequest?.type === 1 ? "Entidade" : "Empresa"}</StyledInputLabel>
                                    <StyledSelect name="select-company"
                                        value={authenticationRequest?.companyId}
                                        error={!fieldIsValid && (authenticationRequest?.companyId === undefined || authenticationRequest?.companyId === 0) ? true : false}
                                        fullWidth 
                                        onChange={handleOnChange}
                                    >
                                        {preAuthenticationResponse?.companies?.map((element) => {
                                            return (
                                                <MenuItem value={element.id} key={element.id}>{element.name} - {element.formattedDocument}</MenuItem>
                                            );
                                        })}
                                    </StyledSelect>
                                </Box>
                                {/* Checkbox e Link "Esqueceu senha" */}
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 1 }}>
                                    <FormControlLabel 
                                        control={
                                            <Checkbox 
                                                checked={authenticationRequest?.remember || false}
                                                name="check-remember" 
                                                onChange={handleOnChange} 
                                                disabled={preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 ? true : false} 
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                }}
                                            />
                                        } 
                                        label="Lembrar" 
                                        sx={{ color: 'text.secondary' }}
                                    />
                                    <Box sx={{ flex: '1 1 auto' }} />
                                    <Link 
                                        sx={{ 
                                            color: 'primary.main',
                                            textDecoration: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }} 
                                        href="#"
                                    >
                                        Esqueceu sua senha?
                                    </Link>
                                </Box>
                                {/* Botões de ação */}
                                <ActionsContainer sx={{ pt: 2 }}>
                                    {preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 ?
                                        (
                                            <LoadingButton
                                                loading={loading}
                                                loadingPosition="start"
                                                startIcon={<ArrowCircleLeft />}
                                                onClick={() => {
                                                    resetState();
                                                }}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    px: 3,
                                                    py: 1.5
                                                }}
                                            >
                                                Voltar
                                            </LoadingButton>) : (null)
                                    }
                                    <LoadingButton
                                        loading={loading}
                                        variant="contained"
                                        loadingPosition="end"
                                        endIcon={(applicationData !== undefined && preAuthenticationRequest?.type === 2) || (preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 && preAuthenticationRequest?.type === 1) ? <CheckCircleIcon /> : <ArrowCircleRightIcon />}
                                        onClick={authentication}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 4,
                                            py: 1.5,
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46a3 100%)',
                                                boxShadow: '0 12px 24px rgba(102, 126, 234, 0.4)'
                                            }
                                        }}
                                    >
                                        {(applicationData !== undefined && preAuthenticationRequest?.type === 2) || (preAuthenticationResponse != null && preAuthenticationResponse?.companies?.length > 0 && preAuthenticationRequest?.type === 1) ? "Entrar"
                                            : "Próximo"}
                                    </LoadingButton>
                                </ActionsContainer>
                            </FormContainer>
                        </StyledCardContent>
                    </GlassCard>
                </CenteredContainer>
            </GradientBackground>
        </React.Fragment>
    )
}